import { AxiosInstance } from 'axios';
import { Language, Receipt, generateConversionErrorMessage, mapReplacer } from '..';
import i18n from '../../../client/src/i18n/i18n';
import { NotificationType } from '../notification/notification.types';
import { generateNotification } from '../notification/notification.util';
import {
  InvoiceMailConfiguration,
  MailConfiguration,
  MailServiceReport,
  MailTemplate,
  MailType,
  OrderConfirmationMailConfiguration,
  ProtocolMailConfig,
} from './Mail.types';

/**
 * API Method to send a testMail with a new smtpConfiguration
 * @param axios instance of axios
 * @param config containing the sender configuration and the receiver
 * @returns boolean if the test was succesfull
 */
export const sendTestMail = async (
  axios: AxiosInstance,
  config: MailConfiguration,
): Promise<boolean> => {
  return axios
    .post('/mail/test', config)
    .then((res) => {
      res.status === 200 &&
        generateNotification({
          type: NotificationType.SUCCESS,
          value: i18n.t('general.notification.success.testMail'),
        });
      return res.status === 200;
    })
    .catch((exc) => {
      console.error('Error while sending testmail', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.testMail'),
      });
      return false;
    });
};

/**
 * API method to send an orderConfirmation
 * @param axios instance of axios
 * @param config neccessary mailConfig
 * @returns
 */
export const sendOrderConfirmation = async (
  axios: AxiosInstance,
  config: OrderConfirmationMailConfiguration,
): Promise<boolean> => {
  return axios
    .post('/mail/confirmation', config)
    .then((res) => {
      res.status === 200 &&
        generateNotification({
          type: NotificationType.SUCCESS,
          value: i18n.t('general.notification.success.orderConfirmation'),
        });
      return res.status === 200;
    })
    .catch((exc) => {
      console.error('Error while sending orderConfirmation', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.orderConfirmation'),
      });
      return false;
    });
};

/**
 * API Method to send a message per Mail andset the invoiceState
 * @param axios  instance of Axios
 * @param config InvoiceMailConfiguration containing all neccessary information
 * @returns
 */
export const sendInvoiceMail = async (
  axios: AxiosInstance,
  config: InvoiceMailConfiguration,
): Promise<Receipt> => {
  return axios
    .post('/mail/invoice', config)
    .then((res) => {
      return res.data;
    })
    .catch((exc) => {
      if (exc.status === 422) generateConversionErrorMessage();
      else
        generateNotification({
          type: NotificationType.ERROR,
          value: i18n.t('general.notification.error.sendInvoiceMail'),
        });
      console.error('Error while sending invoice', exc);
      return undefined;
    });
};
/**
 * API Method to send a message per Mail andset the invoiceState
 * @param axios  instance of Axios
 * @param config InvoiceMailConfiguration containing all neccessary information
 * @returns Promise with no content
 */
export const sendProtocolMail = async (
  axios: AxiosInstance,
  config: ProtocolMailConfig,
): Promise<void> => {
  return axios
    .post('/mail/protocol', config)
    .then(() => {
      generateNotification({
        type: NotificationType.SUCCESS,
        value: i18n.t('general.notification.success.sendProtocol'),
      });
    })
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.sendProtocolMail'),
      });
      console.error('Error while sending invoice', exc);
      return undefined;
    });
};

/**
 * API method to get a mailTemplate by type
 * @param axios instance of axios
 * @param type MailType to load
 * @returns  found or created mailTemplate or undefined on error
 */
export const getMailTemplateForMailType = async (
  axios: AxiosInstance,
  type: MailType,
): Promise<MailTemplate | undefined> => {
  return axios
    .get('/mail/template/type', { params: { type } })
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getMailTemplateForMailType'),
      });
      console.error('Error while loading mailTemplate', exc);
      return undefined;
    });
};

/**
 * Util method to get a mailTemplate with the populated values
 * @param axios instance of axios
 * @param type mailtype to get the template for
 * @param referenceId that is neccessary to load the correct data
 * @param userId that creates the template
 * @param language to get the template in
 * @returns
 */
export const getPopulatedMailTemplate = async (
  axios: AxiosInstance,
  type: MailType,
  referenceId: string,
  userId: string,
  language: Language,
): Promise<MailTemplate | undefined> => {
  return axios
    .get('/mail/template/type/populated', {
      params: { type, referenceId, userId, language },
    })
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading mailtemplate', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getMailTemplateForMailType'),
      });
      return undefined;
    });
};

/**
 * API method to update a MailTemplate
 * @param axios instance of axios
 * @param template to update
 * @returns updated mailTemplate or undefined on error
 */
export const updateMailTemplate = async (
  axios: AxiosInstance,
  template: MailTemplate,
): Promise<MailTemplate | undefined> => {
  return axios
    .post('/mail/template/update', JSON.stringify(template, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.updateMailTemplate'),
      });
      console.error('Error while updating mailTemplate', exc);
      return undefined;
    });
};

/**
 * API method to create a new mailTemplate
 * @param axios  instance of axios
 * @param template  to create
 * @returns  Created template or undefined on error
 */
export const createNewMailTemplate = async (
  axios: AxiosInstance,
  template: MailTemplate,
): Promise<MailTemplate | undefined> => {
  return axios
    .post('/mail/template/', JSON.stringify(template, mapReplacer))
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.createNewMailTemplate'),
      });
      console.error('Error while updating mailTemplate', exc);
      return undefined;
    });
};

/**
 * API Method to load the mailReport
 * @param axios instance of axios
 * @param date the date to load the report
 * @returns Found report or undefined on error
 */
export const getMailReportForDate = async (
  axios: AxiosInstance,
  date: Date,
): Promise<MailServiceReport | undefined> => {
  return axios
    .get('/mail/report/', { params: { date: date.toISOString().split('T')[0] } })
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading mailReport', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getMailReport'),
      });
      return undefined;
    });
};
